import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { NameCell } from '../../hooks/useNameMaps';
import { Alert, Button, Card, SearchableSelect, StatusBadge, Tabs, useToast } from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import type { Store, StoreCreate } from '@kss/api';
import { storeService } from '@kss/api';
import { loadCompanyOptions } from '../shared/formLoaders';
import { validateGst, validateMobile } from '../../utils/formatValidators';
import { labelForCountry, labelForState } from '../../utils/indiaLocations';
import AddressFields from '../../components/AddressFields';

/**
 * Store form — fields rewritten 2026-05-02 to match the live API
 * shape exactly. The backend stores Indian-style addresses split
 * across `addressLine1, city, state, postalCode, country`; previously
 * we sent a single `address` string which the API silently dropped.
 */

type TabStatus = 'default' | 'danger' | 'success' | 'warning';
type FormState = {
  name: string;
  code: string;
  gstNumber: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  companyId: string;
  /* Delivery terms. Held as strings so an empty box stays empty rather
     than snapping to 0 — "no free-shipping tier" and "free above ₹0"
     are different rules and the form must be able to express both. */
  shippingCharge: string;
  freeShippingThreshold: string;
  isActive: boolean;
};
const emptyForm: FormState = {
  name: '',
  code: '',
  gstNumber: '',
  phone: '',
  addressLine1: '',
  city: '',
  state: '',
  postalCode: '',
  // Live API has `country varchar(5)`. Save the ITU-T E.164 calling
  // code ("91" for India, "1" for US/Canada, "44" for UK, …) so the
  // value fits the column AND carries semantic info that pairs
  // naturally with phone numbers. See COUNTRIES in indiaLocations.ts.
  country: '91',
  companyId: '',
  /* Blank, not '0' — a new store quotes no delivery fee until the
     merchant sets one. The storefront previously charged ₹99 whether or
     not anyone had configured it. */
  shippingCharge: '',
  freeShippingThreshold: '',
  isActive: true,
};

const STEPS = ['basic', 'address', 'settings', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic info',
  address: 'Address',
  settings: 'Settings',
  review: 'Review & save',
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function StoreForm() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('basic');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setLoadError(null);
        try {
          const rec = await storeService.get(id);
          if (cancelled) return;
          setForm({
            name: rec.name ?? '',
            code: rec.code ?? '',
            gstNumber: rec.gstNumber ?? '',
            phone: rec.phone ?? '',
            addressLine1: rec.addressLine1 ?? rec.address ?? '',
            city: rec.city ?? '',
            state: rec.state ?? '',
            postalCode: rec.postalCode ?? '',
            country: rec.country ?? '91',
            companyId: rec.companyId ?? '',
            /* `?? ''` on a 0 would blank the box, so test for null
               explicitly: a store with the fee deliberately set to 0
               must round-trip as "0", not as "unset". */
            shippingCharge: rec.shippingCharge == null ? '' : String(rec.shippingCharge),
            freeShippingThreshold:
              rec.freeShippingThreshold == null ? '' : String(rec.freeShippingThreshold),
            isActive: !!rec.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else setForm(emptyForm);
      setErrors({});
      setStep('basic');
      setRedTabs(new Set());
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const blurValidate = <K extends keyof FormState>(
    key: K,
    label: string,
    validator: (v: string) => string | null,
  ) => (e: React.FocusEvent<HTMLInputElement>) => {
    const err = validator(e.currentTarget.value);
    if (err) {
      toast.error(`Invalid ${label}`, err);
      set(key, '' as FormState[K]);
    }
  };

  const validateStep = (s: StepId): boolean => {
    const errs: typeof errors = {};
    if (s === 'basic' || s === 'review') {
      if (!form.name.trim()) errs.name = 'Name is required';
      const gstErr = validateGst(form.gstNumber);
      if (gstErr) errs.gstNumber = gstErr;
      const phoneErr = validateMobile(form.phone);
      if (phoneErr) errs.phone = phoneErr;
    }
    if (s === 'settings' || s === 'review') {
      // Live API rejects Store inserts without `companyId` (NOT NULL FK
      // — swagger marks it optional but the DB constraint is real and
      // the server returns a generic 500 EF message on violation).
      // Block at form-level until the backend fixes the schema.
      if (!form.companyId) errs.companyId = 'Company is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    const i = STEPS.indexOf(step);
    if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) {
      toast.error('Fix errors', 'Some fields are invalid');
      return;
    }
    const next = STEPS[i + 1]!;
    setRedTabs((p) => {
      const n = new Set(p);
      n.delete(next);
      return n;
    });
    setStep(next);
  };
  const goPrev = () => {
    const i = STEPS.indexOf(step);
    if (i === 0) return;
    setRedTabs((p) => new Set(p).add(step));
    setStep(STEPS[i - 1]!);
  };

  const basicValid = !!form.name.trim();

  const handleSave = async () => {
    // Tab-wise validation: only validate fields visible on the
    // current tab. On non-final tabs Save & Next just advances —
    // we don't call the API until the user reaches the Review tab.
    // This is what "mandatory should be tab-wise" means: the
    // Company picker on the Settings tab can't block Save & Next
    // on the Basic tab.
    if (!validateStep(step)) {
      toast.error('Fix errors', 'Some fields are invalid');
      return;
    }
    const i = STEPS.indexOf(step);
    const isLastTab = i === STEPS.length - 1;
    if (!isLastTab) {
      // Intermediate tab — advance without calling the backend.
      // Validation for later tabs runs when the user clicks Next
      // on those tabs (or reaches Review).
      const next = STEPS[i + 1]!;
      setRedTabs((p) => {
        const n = new Set(p);
        n.delete(next);
        return n;
      });
      setStep(next);
      return;
    }
    // Final tab (Review) — full validation across all tabs, then
    // the actual API save.
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    if (!form.companyId) {
      toast.error('Company required', 'Pick a company on the Settings tab — backend rejects stores without one.');
      setStep('settings');
      return;
    }
    setSaving(true);
    try {
      const body: Store = {
        storeId: isEdit ? id : undefined,
        name: form.name,
        code: form.code || undefined,
        gstNumber: form.gstNumber ? form.gstNumber.toUpperCase() : undefined,
        phone: form.phone || undefined,
        addressLine1: form.addressLine1 || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country || undefined,
        companyId: form.companyId || undefined,
        /* Blank → 0: the column is non-nullable and 0 is the correct
           reading of "no delivery fee". */
        shippingCharge: form.shippingCharge.trim() === '' ? 0 : Number(form.shippingCharge),
        /* Blank → null, which the backend reads as "no free-delivery
           tier". Distinct from 0, which would mean "free at any
           subtotal" — see the field's own hint. */
        freeShippingThreshold:
          form.freeShippingThreshold.trim() === ''
            ? null
            : Number(form.freeShippingThreshold),
        isActive: form.isActive,
      };
      if (isEdit && id) {
        await storeService.update([body]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        const { storeId: _o, ...create } = body;
        // Live `POST /api/Store` returns `data: true` (boolean) instead of
        // the created entity, so `created[0].storeId` is always undefined.
        // Recover the id by filtering on the unique `code` (or `name` when
        // `code` is blank) right after the create resolves. See
        // BACKEND_AGENT_PROMPTS.md Item 4 — same response-shape bug as
        // SalesOrder, just on a different controller.
        await storeService.create([create as StoreCreate]);
        toast.success('Saved', `${form.name} added`);
      }
      // Review-tab save succeeded (create or update) — kick the user
      // back to the list so they see the freshly-created/edited row in
      // context. Matches edit-mode behaviour.
      navigate('/panel/stores');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => {
    const m: Record<string, TabStatus> = {};
    redTabs.forEach((s) => (m[s] = 'danger'));
    return m;
  }, [redTabs]);

  const field = (
    label: string,
    key: keyof FormState,
    input: React.ReactNode,
    required?: boolean,
    /** Explanatory line under the input. Hidden while an error shows,
        so the two never stack and compete for the same glance. */
    hint?: string,
  ) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">
        {label} {required && <span className="text-danger">*</span>}
      </span>
      {input}
      {errors[key] ? (
        <span className="text-xs text-danger">{errors[key]}</span>
      ) : (
        hint && <span className="text-xs text-content-muted">{hint}</span>
      )}
    </label>
  );

  const basicStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Name', 'name', <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="ACME Bandra Flagship" className={inputCls} />, true)}
      {field('Code', 'code', <input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="STR-001" className={inputCls} />)}
      {field(
        'GST number',
        'gstNumber',
        <input
          value={form.gstNumber}
          onChange={(e) => set('gstNumber', e.target.value.toUpperCase())}
          onBlur={blurValidate('gstNumber', 'GST', validateGst)}
          placeholder="27AAAPL1234C1ZA"
          maxLength={15}
          className={`${inputCls} font-mono`}
        />,
      )}
      {field(
        'Phone',
        'phone',
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
          onBlur={blurValidate('phone', 'mobile number', validateMobile)}
          placeholder="+91 9876543210"
          maxLength={15}
          className={inputCls}
        />,
      )}
    </div>
  );

  const addressStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {/* Single shared component for the address block — pincode
          auto-fills city/state/country via api.postalpincode.in. */}
      <AddressFields
        addressLine1={form.addressLine1}
        setAddressLine1={(v) => set('addressLine1', v)}
        pincode={form.postalCode}
        setPincode={(v) => set('postalCode', v)}
        city={form.city}
        setCity={(v) => set('city', v)}
        state={form.state}
        setState={(v) => set('state', v)}
        country={form.country}
        setCountry={(v) => set('country', v)}
      />
    </div>
  );

  const settingsStep = (
    <div className="grid gap-3 md:grid-cols-2">
      <SearchableSelect
        label="Company *"
        value={form.companyId}
        onChange={(v) => set('companyId', v)}
        loadOptions={loadCompanyOptions}
        placeholder="Select a company"
        searchPlaceholder="Search companies..."
        error={errors.companyId}
      />
      {field(
        'Status',
        'isActive',
        <select
          value={form.isActive ? 'active' : 'inactive'}
          onChange={(e) => set('isActive', e.target.value === 'active')}
          className={selectCls}
        >
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>,
      )}
      {/* Delivery terms — quoted by the storefront cart and checkout.
          Only the FIRST active store (ordered by code) drives the
          public storefront today, which is why the hint says so: a
          merchant editing their second store would otherwise expect a
          change that never shows up. */}
      {field(
        'Delivery fee',
        'shippingCharge',
        <input
          type="number"
          min={0}
          step="0.01"
          value={form.shippingCharge}
          onChange={(e) => set('shippingCharge', e.target.value)}
          placeholder="0.00"
          className={selectCls}
        />,
        false,
        'Flat fee added at checkout. Blank or 0 = delivery is always free.',
      )}
      {field(
        'Free delivery above',
        'freeShippingThreshold',
        <input
          type="number"
          min={0}
          step="0.01"
          value={form.freeShippingThreshold}
          onChange={(e) => set('freeShippingThreshold', e.target.value)}
          placeholder="No free-delivery tier"
          className={selectCls}
        />,
        false,
        'Order subtotal at or above which the fee is waived. Blank = the fee always applies.',
      )}
    </div>
  );

  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-sm font-medium">Ready to save</span>
      </div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Name" value={form.name} />
          <Row label="Code" value={form.code} />
          <Row label="GST" value={form.gstNumber} />
          <Row label="Phone" value={form.phone} />
          <Row label="Address line 1" value={form.addressLine1} />
          <Row label="City" value={form.city} />
          {/* Display the readable state / country names instead of
              the GST / ITU calling-code values that the form
              persists. `labelForState('27')` → "Maharashtra";
              `labelForCountry('91')` → "India". Unknown codes
              fall through to the raw value. */}
          <Row label="State" value={labelForState(form.state)} />
          <Row label="Postal code" value={form.postalCode} />
          <Row label="Country" value={labelForCountry(form.country)} />
          <Row label="Company" value={<NameCell kind="company" id={form.companyId} />} />
          <Row label="Status" value={<StatusBadge status={form.isActive ? 'active' : 'inactive'} />} />
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic' as const, label: STEP_LABELS.basic, content: basicStep },
    { id: 'address' as const, label: STEP_LABELS.address, content: addressStep },
    { id: 'settings' as const, label: STEP_LABELS.settings, content: settingsStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs
          tabs={tabs}
          activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]!); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]!); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus}
          barOnly
        />
      </div>
      <Card>
        <div className="min-h-[180px]">
          {loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}>
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            {!isLast && (
              <Button type="button" onClick={goNext}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/stores')}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={!basicValid || saving}>
              <Save className="h-4 w-4" />{' '}
              {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">
        {value || <em className="text-content-muted">—</em>}
      </span>
    </div>
  );
}
